import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSiteOptions,
  updateSiteOptions,
  clearSiteOptionsErrors,
} from "../../../redux/actions/siteOptions";
import { toast } from "react-toastify";
import {
  AiOutlinePlus,
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineClose,
} from "react-icons/ai";

const slug = "global";
const LEGACY_GROUPS = [
  { key: "brands", label: "Brands" },
  { key: "colors", label: "Colours" },
  { key: "weaponPlatforms", label: "Weapon Platforms" },
  { key: "externalPartTypes", label: "External Part Types" },
];

const toFilterKey = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeGroupsFromFilters = (productFilters = {}) => {
  const existingGroups = Array.isArray(productFilters.groups)
    ? productFilters.groups
    : [];
  if (existingGroups.length > 0) {
    return existingGroups.map((group, groupIndex) => ({
      key: toFilterKey(group?.key || `filter_${groupIndex + 1}`),
      label: String(group?.label || `Filter ${groupIndex + 1}`).trim(),
      options: Array.isArray(group?.options)
        ? group.options
            .map((item, optionIndex) => ({
              name: String(item?.name || "").trim(),
              order:
                typeof item?.order === "number" ? item.order : optionIndex,
            }))
            .filter((item) => item.name)
        : [],
      visibleCategories: Array.isArray(group?.visibleCategories)
        ? group.visibleCategories.map((item) => String(item || "").trim()).filter(Boolean)
        : [],
      order: typeof group?.order === "number" ? group.order : groupIndex,
      isActive: group?.isActive !== false,
    }));
  }

  return LEGACY_GROUPS.map((group, groupIndex) => {
    const options = Array.isArray(productFilters[group.key])
      ? productFilters[group.key]
          .map((item, optionIndex) => ({
            name: String(item?.name || "").trim(),
            order: typeof item?.order === "number" ? item.order : optionIndex,
          }))
          .filter((item) => item.name)
      : [];
    const visibleCategories = Array.isArray(productFilters?.visibility?.[group.key])
      ? productFilters.visibility[group.key].map((item) => String(item || "").trim()).filter(Boolean)
      : [];
    return {
      key: group.key,
      label: group.label,
      options,
      visibleCategories,
      order: groupIndex,
      isActive: true,
    };
  });
};

const ProductFiltersOptions = () => {
  const dispatch = useDispatch();
  const { options, loading, updating, updateErrors, updateSuccess } =
    useSelector((state) => state.siteOptions);

  const productFilters = options[slug]?.productFilters || {};
  const isLoading = loading[slug];
  const isUpdating = updating[slug];
  const updateError = updateErrors[slug];
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [newOption, setNewOption] = useState("");

  useEffect(() => {
    if (!options[slug] && !isLoading) {
      dispatch(fetchSiteOptions(slug));
    }
  }, [dispatch, slug, options, isLoading]);

  useEffect(() => {
    const normalized = normalizeGroupsFromFilters(productFilters)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((group, index) => ({
        ...group,
        order: index,
        options: [...group.options].sort((a, b) => (a.order || 0) - (b.order || 0)),
      }));
    setGroups(normalized);
  }, [productFilters]);

  useEffect(() => {
    if (updateSuccess[slug]) {
      toast.success("Product filter settings saved");
    }
  }, [updateSuccess, slug]);

  useEffect(() => {
    if (updateError) {
      toast.error(updateError);
      dispatch(clearSiteOptionsErrors());
    }
  }, [updateError, dispatch]);

  const save = () => {
    const normalizedGroups = groups.map((group, groupIndex) => ({
      key: toFilterKey(group.key || group.label || `filter_${groupIndex + 1}`),
      label: String(group.label || "").trim(),
      options: (group.options || [])
        .map((item, optionIndex) => ({
          name: String(item?.name || "").trim(),
          order: optionIndex,
        }))
        .filter((item) => item.name),
      visibleCategories: Array.isArray(group.visibleCategories)
        ? group.visibleCategories.map((item) => String(item || "").trim()).filter(Boolean)
        : [],
      order: groupIndex,
      isActive: true,
    }));
    const groupsByKey = normalizedGroups.reduce((acc, group) => {
      acc[group.key] = group;
      return acc;
    }, {});
    const toLegacyOptions = (key) =>
      groupsByKey[key]?.options?.map((item, index) => ({
        name: item.name,
        order: index,
      })) || [];
    const toLegacyVisibility = (key) => groupsByKey[key]?.visibleCategories || [];

    dispatch(
      updateSiteOptions(slug, {
        productFilters: {
          brands: toLegacyOptions("brands"),
          colors: toLegacyOptions("colors"),
          weaponPlatforms: toLegacyOptions("weaponPlatforms"),
          externalPartTypes: toLegacyOptions("externalPartTypes"),
          visibility: {
            brands: toLegacyVisibility("brands"),
            colors: toLegacyVisibility("colors"),
            weaponPlatforms: toLegacyVisibility("weaponPlatforms"),
            externalPartTypes: toLegacyVisibility("externalPartTypes"),
          },
          groups: normalizedGroups,
        },
      })
    );
  };
  const addGroup = () => {
    const label = newGroupName.trim();
    if (!label) return;
    const key = toFilterKey(label);
    if (!key) {
      toast.error("Please enter a valid filter name");
      return;
    }
    if (groups.some((group) => group.key === key)) {
      toast.info("A filter with this name already exists");
      return;
    }
    const next = [
      ...groups,
      {
        key,
        label,
        options: [],
        visibleCategories: [],
        order: groups.length,
        isActive: true,
      },
    ];
    setGroups(next);
    setNewGroupName("");
  };

  const removeGroup = (index) => {
    setGroups((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((group, order) => ({ ...group, order }))
    );
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewOption("");
    }
  };

  const updateGroupLabel = (index, nextLabel) => {
    setGroups((prev) => {
      const trimmed = nextLabel.trim();
      if (!trimmed) return prev;
      const key = toFilterKey(trimmed);
      if (!key) return prev;
      if (prev.some((group, i) => i !== index && group.key === key)) {
        toast.info("Another filter already uses this name");
        return prev;
      }
      const copy = [...prev];
      copy[index] = { ...copy[index], label: trimmed, key };
      return copy;
    });
  };

  const addOptionToGroup = () => {
    const value = newOption.trim();
    if (!value || editingIndex === null) return;
    setGroups((prev) => {
      const copy = [...prev];
      const group = copy[editingIndex];
      if (!group) return prev;
      if ((group.options || []).some((item) => item.name.toLowerCase() === value.toLowerCase())) {
        toast.info("Option already exists");
        return prev;
      }
      copy[editingIndex] = {
        ...group,
        options: [...(group.options || []), { name: value, order: group.options.length }],
      };
      return copy;
    });
    setNewOption("");
  };

  const removeOptionFromGroup = (optionIndex) => {
    if (editingIndex === null) return;
    setGroups((prev) => {
      const copy = [...prev];
      const group = copy[editingIndex];
      if (!group) return prev;
      copy[editingIndex] = {
        ...group,
        options: (group.options || [])
          .filter((_, i) => i !== optionIndex)
          .map((item, i) => ({ ...item, order: i })),
      };
      return copy;
    });
  };

  const toggleCategory = (categorySlug) => {
    if (editingIndex === null) return;
    setGroups((prev) => {
      const copy = [...prev];
      const group = copy[editingIndex];
      if (!group) return prev;
      const current = Array.isArray(group.visibleCategories) ? group.visibleCategories : [];
      const exists = current.includes(categorySlug);
      copy[editingIndex] = {
        ...group,
        visibleCategories: exists
          ? current.filter((item) => item !== categorySlug)
          : [...current, categorySlug],
      };
      return copy;
    });
  };

  const discardChanges = () => {
    const normalized = normalizeGroupsFromFilters(productFilters)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((group, index) => ({
        ...group,
        order: index,
      }));
    setGroups(normalized);
    setEditingIndex(null);
    setNewOption("");
  };

  const catalogCategories = options[slug]?.catalog?.categories || [];
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [groups]
  );
  const editingGroup = editingIndex === null ? null : sortedGroups[editingIndex];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-[#38513b]">Product Filters (Filter Settings)</h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage filters used for products, including which categories each filter appears in.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
        <h3 className="text-base font-medium text-slate-800">Create filter group</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addGroup())}
            placeholder="e.g. Material, Style, Calibre"
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
          />
          <button
            type="button"
            onClick={addGroup}
            className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#38513b] px-4 py-2 text-sm font-medium text-white hover:bg-[#2f4232]"
          >
            <AiOutlinePlus className="h-4 w-4" />
            Add filter
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {sortedGroups.length === 0 && (
          <div className="col-span-full rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">
            No filter groups yet. Add your first filter above.
          </div>
        )}
        {sortedGroups.map((group, index) => (
          <div key={`${group.key}-${index}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <input
                  type="text"
                  value={group.label}
                  onChange={(e) => updateGroupLabel(index, e.target.value)}
                  className="w-full rounded border border-transparent px-0 py-0.5 text-base font-semibold text-slate-900 focus:border-[#38513b] focus:bg-white focus:outline-none"
                />
                <p className="text-xs text-slate-500 mt-1">Key: {group.key}</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setEditingIndex(index);
                    setNewOption("");
                  }}
                  className="inline-flex items-center justify-center rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                  title="Edit filter"
                >
                  <AiOutlineEdit className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeGroup(index)}
                  className="inline-flex items-center justify-center rounded-md border border-red-200 p-2 text-red-600 hover:bg-red-50"
                  title="Delete filter"
                >
                  <AiOutlineDelete className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-md bg-slate-50 p-2">
                <p className="text-slate-500">Options</p>
                <p className="text-slate-900 font-semibold">{group.options?.length || 0}</p>
              </div>
              <div className="rounded-md bg-slate-50 p-2">
                <p className="text-slate-500">Categories</p>
                <p className="text-slate-900 font-semibold">
                  {group.visibleCategories?.length || 0}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={discardChanges}
          disabled={isUpdating}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Discard changes
        </button>
        <button
          type="button"
          onClick={save}
          disabled={isUpdating}
          className="rounded-lg bg-[#38513b] px-4 py-2 text-sm font-medium text-white hover:bg-[#2f4232] disabled:opacity-50"
        >
          {isUpdating ? "Saving..." : "Save filter settings"}
        </button>
      </div>

      {editingGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Edit filter: {editingGroup.label}
                </h3>
                <p className="text-sm text-slate-500">
                  Add/remove options and choose where this filter should appear.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingIndex(null)}
                className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
              >
                <AiOutlineClose className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-6 p-5">
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Options</h4>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addOptionToGroup())
                    }
                    placeholder="Add option value"
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                  />
                  <button
                    type="button"
                    onClick={addOptionToGroup}
                    className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#38513b] px-3 py-2 text-sm font-medium text-white hover:bg-[#2f4232]"
                  >
                    <AiOutlinePlus className="h-4 w-4" />
                    Add option
                  </button>
                </div>
                <ul className="max-h-[240px] overflow-y-auto space-y-1">
                  {(editingGroup.options || []).length === 0 && (
                    <li className="text-sm text-slate-500 py-2">No options added yet.</li>
                  )}
                  {(editingGroup.options || []).map((item, optionIndex) => (
                    <li
                      key={`${item.name}-${optionIndex}`}
                      className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2"
                    >
                      <span className="text-sm text-slate-800">{item.name}</span>
                      <button
                        type="button"
                        onClick={() => removeOptionFromGroup(optionIndex)}
                        className="p-1 text-slate-400 hover:text-red-600"
                      >
                        <AiOutlineDelete className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-1">
                  Category visibility
                </h4>
                <p className="text-xs text-slate-500 mb-3">
                  If no categories are selected, this filter appears in all categories.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {catalogCategories.map((cat) => {
                    const slugValue = cat.slug || cat.name || "";
                    const checked = (editingGroup.visibleCategories || []).includes(slugValue);
                    return (
                      <label
                        key={slugValue}
                        className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCategory(slugValue)}
                        />
                        <span>{cat.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <p className="text-sm text-slate-500">Loading options…</p>
      )}
    </div>
  );
};

export default ProductFiltersOptions;
